// The Studio — games launcher ("hub"). A premium front door: the visitor picks one game
// from the PROMOTED list and launches it. No game logic lives here; each card links to the
// game's own deployed app at `${GAME_BASE}${slug}/`.
//
// Built on @studio/kit. CSP-safe throughout: every visual is in external CSS; per-game accents
// are written at runtime via el.style.setProperty (CSSOM property writes, allowed under a strict
// CSP); state is driven by classes / data-* attributes; all motion is WAAPI; no inline <style>
// or style attributes, no inline scripts.

import '@studio/kit/tokens.css';
import '@studio/kit/reset.css';
import './style.css';

import { Theme, announce, animate, prefersReducedMotion } from '@studio/kit';
import { PROMOTED, type GameEntry } from './catalog.js';

// ────────────────────────────────────────────────────────────────────────────
// PLAY TARGET. Each card links to `${GAME_BASE}${slug}/` as a RELATIVE href, so it resolves
// whether the hub is served at the site root (local `npm run play` → /spectra/) OR under a
// project-pages subpath (GitHub Pages → /daily-games/spectra/). Keep it relative (no leading
// slash) unless you deploy games on a different origin — then set an absolute base like
// 'https://cdn.example.com/games/'.
const GAME_BASE = '';

function hrefFor(slug: string): string {
  return `${GAME_BASE}${slug}/`;
}

// ────────────────────────────────────────────────────────────────────────────
// Tiny DOM helper — no innerHTML for dynamic data (CSP-clean, XSS-proof).
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const c of children) node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
function svg(tag: string, attrs: Record<string, string> = {}, children: Element[] = []): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const c of children) node.append(c);
  return node;
}

// ────────────────────────────────────────────────────────────────────────────
// Geometric identity marks. One per game — a simple, deliberate motif (NOT amateur art),
// drawn from the kit's geometry vocabulary and tinted by the card's own accent via
// currentColor (set on the wrapping <svg> through the --game-accent CSSOM write).
// Each returns an <svg> sized 0..48 in a 0 0 48 48 viewBox.

type MarkFn = () => SVGElement;

function wrap(...kids: Element[]): SVGElement {
  return svg(
    'svg',
    { viewBox: '0 0 48 48', class: 'card__glyph', 'aria-hidden': 'true', focusable: 'false' },
    kids,
  );
}

const MARKS: Record<string, MarkFn> = {
  // Spectra — a chromatic arc of chips, one offset (the odd one out).
  spectra: () =>
    wrap(
      svg('circle', { cx: '12', cy: '30', r: '5', fill: 'currentColor', opacity: '0.35' }),
      svg('circle', { cx: '24', cy: '24', r: '5', fill: 'currentColor', opacity: '0.6' }),
      svg('circle', { cx: '36', cy: '30', r: '5', fill: 'currentColor', opacity: '0.35' }),
      svg('circle', { cx: '24', cy: '12', r: '6', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }),
    ),
  // Vault — concentric dial rings with a notch.
  vault: () =>
    wrap(
      svg('circle', { cx: '24', cy: '24', r: '16', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.4' }),
      svg('circle', { cx: '24', cy: '24', r: '9', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.5' }),
      svg('line', { x1: '24', y1: '8', x2: '24', y2: '15', stroke: 'currentColor', 'stroke-width': '3', 'stroke-linecap': 'round' }),
    ),
  // Wane — interlocking gears (the watch movement).
  wane: () =>
    wrap(
      svg('circle', { cx: '18', cy: '24', r: '10', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.5' }),
      svg('circle', { cx: '18', cy: '24', r: '3', fill: 'currentColor' }),
      svg('circle', { cx: '34', cy: '16', r: '6', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.55' }),
    ),
  // Lumen — a beam refracting through a window frame.
  lumen: () =>
    wrap(
      svg('rect', { x: '10', y: '10', width: '12', height: '28', rx: '2', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.5' }),
      svg('path', { d: 'M22 16 L40 24 L22 32', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.5', 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }),
      svg('circle', { cx: '40', cy: '24', r: '2.5', fill: 'currentColor' }),
    ),
  // Fold — a sheet creasing along a diagonal.
  fold: () =>
    wrap(
      svg('path', { d: 'M12 12 H36 V36 H12 Z', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.45' }),
      svg('path', { d: 'M12 12 L36 36', stroke: 'currentColor', 'stroke-width': '2.5', 'stroke-linecap': 'round' }),
      svg('path', { d: 'M36 12 L24 24', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.6', 'stroke-linecap': 'round' }),
    ),
  // Resonance — stacked waveform bars.
  resonance: () =>
    wrap(
      svg('rect', { x: '10', y: '20', width: '5', height: '8', rx: '2', fill: 'currentColor', opacity: '0.5' }),
      svg('rect', { x: '18', y: '12', width: '5', height: '24', rx: '2', fill: 'currentColor' }),
      svg('rect', { x: '26', y: '16', width: '5', height: '16', rx: '2', fill: 'currentColor', opacity: '0.75' }),
      svg('rect', { x: '34', y: '22', width: '5', height: '4', rx: '2', fill: 'currentColor', opacity: '0.5' }),
    ),
  // Birdsong — a rising pitch-contour with a note.
  birdsong: () =>
    wrap(
      svg('path', { d: 'M10 34 Q20 30 24 22 T40 12', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.5', 'stroke-linecap': 'round' }),
      svg('circle', { cx: '40', cy: '12', r: '3.5', fill: 'currentColor' }),
      svg('circle', { cx: '10', cy: '34', r: '2.5', fill: 'currentColor', opacity: '0.55' }),
    ),
  // Ledger — bars filling to a target line.
  ledger: () =>
    wrap(
      svg('rect', { x: '10', y: '24', width: '6', height: '14', rx: '1.5', fill: 'currentColor', opacity: '0.55' }),
      svg('rect', { x: '21', y: '16', width: '6', height: '22', rx: '1.5', fill: 'currentColor' }),
      svg('rect', { x: '32', y: '28', width: '6', height: '10', rx: '1.5', fill: 'currentColor', opacity: '0.55' }),
      svg('line', { x1: '8', y1: '16', x2: '40', y2: '16', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-dasharray': '3 3', opacity: '0.8' }),
    ),
  // Drift — stars mirrored about a hidden axis.
  drift: () =>
    wrap(
      svg('line', { x1: '24', y1: '8', x2: '24', y2: '40', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-dasharray': '3 4', opacity: '0.5' }),
      svg('path', { d: 'M16 18 l1.6 3.4 3.6.4-2.7 2.5.7 3.6-3.2-1.8', fill: 'currentColor' }),
      svg('path', { d: 'M32 30 l-1.6-3.4-3.6-.4 2.7-2.5-.7-3.6 3.2 1.8', fill: 'currentColor', opacity: '0.7' }),
    ),
  // Tuesday — a framed scene held in memory (window with a recalled glyph).
  tuesday: () =>
    wrap(
      svg('rect', { x: '11', y: '12', width: '26', height: '24', rx: '3', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.5' }),
      svg('polygon', { points: '24,17 26.5,23 33,23 27.5,27 29.5,33 24,29.5 18.5,33 20.5,27 15,23 21.5,23', fill: 'currentColor' }),
    ),
};

function markFor(slug: string): SVGElement {
  const fn = MARKS[slug];
  if (fn) return fn();
  // Generic fallback mark (a ringed dot) — keeps the layout intact for any future game.
  return wrap(
    svg('circle', { cx: '24', cy: '24', r: '14', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', opacity: '0.45' }),
    svg('circle', { cx: '24', cy: '24', r: '5', fill: 'currentColor' }),
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Card construction. The whole card is an accessible link (<a>), keyboard-activatable
// natively; Enter activates an anchor by default, and we add Space for parity with buttons.
function buildCard(game: GameEntry, index: number): HTMLAnchorElement {
  const glyphWrap = el('span', { class: 'card__glyphwrap' });
  glyphWrap.append(markFor(game.slug));

  const name = el('span', { class: 'card__name' }, [game.name]);
  const tagline = el('span', { class: 'card__tagline' }, [game.tagline]);
  const chip = el('span', { class: 'card__chip' }, [game.register]);

  const cue = el('span', { class: 'card__cue', 'aria-hidden': 'true' }, ['Play ']);
  cue.append(
    svg('svg', { viewBox: '0 0 16 16', class: 'card__arrow', 'aria-hidden': 'true', focusable: 'false' }, [
      svg('path', { d: 'M3 8 H12 M8 4 L12 8 L8 12', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.8', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }) as unknown as Element,
    ]),
  );

  const body = el('span', { class: 'card__body' }, [name, tagline, chip]);
  const footer = el('span', { class: 'card__footer' }, [cue]);

  const card = el('a', {
    class: 'card',
    href: hrefFor(game.slug),
    'data-slug': game.slug,
    'aria-label': `Play ${game.name} — ${game.tagline}`,
  }, [glyphWrap, body, footer]) as HTMLAnchorElement;

  card.style.setProperty('--card-index', String(index));
  // Per-game accent identity (CSSOM property writes — allowed under strict CSP).
  card.style.setProperty('--card-accent', game.accent);
  card.style.setProperty('--card-accent-2', game.accent2 ?? game.accent);

  // Space-to-activate parity with buttons (Enter already activates anchors).
  card.addEventListener('keydown', (e) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      card.click();
    }
  });

  // Subtle pointer-tracked spotlight: write the cursor position as CSS vars (CSSOM, CSP-safe).
  card.addEventListener('pointermove', (e) => {
    const r = card.getBoundingClientRect();
    card.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    card.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  });

  return card;
}

// ────────────────────────────────────────────────────────────────────────────
// Theme toggle. Uses kit Theme (light/dark/system). The button cycles light ⇄ dark and
// reflects the resolved theme via aria-pressed + an accessible label; the icon is CSS-driven.
function buildThemeToggle(): HTMLButtonElement {
  const sun = svg('svg', { viewBox: '0 0 24 24', class: 'tt__icon tt__icon--sun', 'aria-hidden': 'true', focusable: 'false' }, [
    svg('circle', { cx: '12', cy: '12', r: '5', fill: 'currentColor' }),
    svg('g', { stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round' }, [
      svg('line', { x1: '12', y1: '1', x2: '12', y2: '4' }),
      svg('line', { x1: '12', y1: '20', x2: '12', y2: '23' }),
      svg('line', { x1: '1', y1: '12', x2: '4', y2: '12' }),
      svg('line', { x1: '20', y1: '12', x2: '23', y2: '12' }),
      svg('line', { x1: '4', y1: '4', x2: '6', y2: '6' }),
      svg('line', { x1: '18', y1: '18', x2: '20', y2: '20' }),
      svg('line', { x1: '4', y1: '20', x2: '6', y2: '18' }),
      svg('line', { x1: '18', y1: '6', x2: '20', y2: '4' }),
    ]),
  ]);
  const moon = svg('svg', { viewBox: '0 0 24 24', class: 'tt__icon tt__icon--moon', 'aria-hidden': 'true', focusable: 'false' }, [
    svg('path', { d: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z', fill: 'currentColor' }),
  ]);

  const btn = el('button', {
    class: 'theme-toggle',
    type: 'button',
    'aria-label': 'Toggle light and dark theme',
  }, [sun, moon]) as HTMLButtonElement;

  const sync = () => {
    const resolved = Theme.resolved();
    btn.setAttribute('data-resolved', resolved);
    btn.setAttribute('aria-pressed', resolved === 'dark' ? 'true' : 'false');
    btn.setAttribute('title', resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme');
  };

  btn.addEventListener('click', () => {
    const next = Theme.resolved() === 'dark' ? 'light' : 'dark';
    Theme.set(next);
    sync();
    announce(`${next === 'dark' ? 'Dark' : 'Light'} theme`);
  });
  Theme.onChange(sync);
  sync();
  return btn;
}

// ────────────────────────────────────────────────────────────────────────────
// Page assembly.
function render(root: HTMLElement): void {
  // Header --------------------------------------------------------------------
  // Home = the hub itself; './' resolves to the current directory in both deploy contexts.
  const wordmark = el('a', { class: 'wordmark', href: './', 'aria-label': 'The Studio — home' }, []);
  wordmark.append(
    svg('svg', { viewBox: '0 0 24 24', class: 'wordmark__mark', 'aria-hidden': 'true', focusable: 'false' }, [
      svg('circle', { cx: '12', cy: '12', r: '9', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }),
      svg('circle', { cx: '12', cy: '12', r: '3.2', fill: 'currentColor' }),
    ]),
    el('span', { class: 'wordmark__text' }, [
      el('strong', {}, ['Daily']),
      el('span', { class: 'wordmark__sub' }, ['Studio']),
    ]),
  );

  const header = el('header', { class: 'site-header', role: 'banner' }, [
    wordmark,
    buildThemeToggle(),
  ]);


  // Grid ----------------------------------------------------------------------
  const grid = el('div', { class: 'grid', role: 'list', 'aria-label': 'Games' });
  const cards: HTMLAnchorElement[] = [];
  PROMOTED.forEach((game, i) => {
    const item = el('div', { class: 'grid__cell', role: 'listitem' });
    const card = buildCard(game, i);
    item.append(card);
    grid.append(item);
    cards.push(card);
  });

  const main = el('main', { class: 'site-main', id: 'main', role: 'main' }, [grid]);

  const footer = el('footer', { class: 'site-footer', role: 'contentinfo' }, [
    el('span', {}, ['Test environment to play designed daily games.']),
  ]);

  root.append(header, main, footer);

  // Staggered entrance (WAAPI; reduced-motion snaps to final state) ------------
  if (!prefersReducedMotion()) {
    cards.forEach((card, i) => {
      animate(
        card,
        [
          { opacity: 0, transform: 'translateY(14px) scale(0.985)' },
          { opacity: 1, transform: 'translateY(0) scale(1)' },
        ],
        { duration: 520, delay: 80 + i * 55, easing: 'cubic-bezier(0.05, 0.7, 0.1, 1)', fill: 'both' },
      );
    });
  }

  announce(`${PROMOTED.length} games available. Press Tab to browse, Enter to play.`);
}

// ────────────────────────────────────────────────────────────────────────────
// Boot.
Theme.init();
const app = document.getElementById('app');
if (app) render(app);
